import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import secondImage from "../../assets/images/icons/no_results.svg";
import { EndPoints } from "../../Constants/endpoints";
import { IDrug } from "../../interface/GeneralResponse";
import AddPrescriptionSearchDrugCard from "./AddPrescriptionSearchDrugCard";
import { InfiniteScroll, SearchComponent } from "../../components";
import { MedicationLoader } from "../../Share";
import { getDrugDisplayName } from "./utils";

interface AddPrescriptionSearchDrugProps {
  onDrugSelect: (drug: IDrug) => void;
  selectedDrugs: IDrug[];
}

const AddPrescriptionSearchDrug: React.FC<AddPrescriptionSearchDrugProps> = ({
  onDrugSelect,
  selectedDrugs,
}) => {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [drugsData, setDrugsData] = useState<IDrug[]>([]);
  const [customDrugs, setCustomDrugs] = useState<IDrug[]>([]);
  const [hasSearched, setHasSearched] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [currentSearchValue, setCurrentSearchValue] = useState<string>("");

  const allDrugs = [...customDrugs, ...(drugsData ?? [])];

  // const filteredDrugs = allDrugs.filter(
  //   (drug) =>
  //     searchQuery === "" ||
  //     (drug.Name?.toLowerCase() || drug.DrugName?.toLowerCase() || "").includes(
  //       searchQuery.toLowerCase(),
  //     ),
  // );


  const handleSearchClick = (values: any) => {
    setSearchQuery(values);
    setCurrentSearchValue(values);
    setHasSearched(true);
  };

  const handleAddCustomDrug = () => {
    const drugNameToAdd = currentSearchValue.trim();

    if (!drugNameToAdd) return;

    const existingCustomDrug = customDrugs.find(
      (drug) =>
        (drug.Name?.toLowerCase() || "") === drugNameToAdd.toLowerCase(),
    );

    if (existingCustomDrug) {
      onDrugSelect(existingCustomDrug);
    } else {
      const customDrug: any = {
        Name: drugNameToAdd,
      };

      setCustomDrugs((prev) => [...prev, customDrug]);

      onDrugSelect(customDrug);
    }

    setSearchQuery("");
    setCurrentSearchValue("");
    setHasSearched(false);
  };

  const handleDrugSelect = (drug: IDrug) => {
    onDrugSelect(drug);
  };

  const isDrugSelected = (drug: IDrug): boolean => {
    if (!drug.PackageId) {
      return selectedDrugs.some(
        (selectedDrug) => selectedDrug.Name === drug.Name,
      );
    }
    return selectedDrugs.some(
      (selectedDrug) => selectedDrug.PackageId === drug.PackageId,
    );
  };


  useEffect(() => {
    if (searchQuery && drugsData?.length === 0 && !isLoading) {
      setHasSearched(true);
      setCurrentSearchValue(searchQuery);
    }
  }, [drugsData, searchQuery, isLoading]);

  return (
    <div className="border-primary-bg flex max-h-full flex-col gap-3 overflow-hidden border-e-2 pb-12 pe-2 lg:w-1/3">
      <SearchComponent
        className="pt-2"
        inputStyle="!min-w-full"
        searchOn="typing"
        placeholder={t("search_for_drugs")}
        onSearch={handleSearchClick}
        value={searchQuery}
      />

      <div className="flex grow flex-col gap-2 overflow-y-auto pb-4">
        {!currentSearchValue.trim() && allDrugs?.map((drug: IDrug) => (
          <AddPrescriptionSearchDrugCard
            key={drug.PackageId || `custom-${getDrugDisplayName(drug)}`}
            drug={drug}
            onSelect={handleDrugSelect}
            isSelected={isDrugSelected(drug)}
          />
        ))}

        <InfiniteScroll
          Count={7}
          skeletonCount={1}
          endpoint={EndPoints.SearchDrug}
          getAllParams={{ Name: searchQuery || "" }}
          getData={(data) => {
            // const dataArray = Array.isArray(data) ? data : [];
            // const filteredData = dataArray.filter(
            //   (apiDrug: any) =>
            //     !customDrugs.some(
            //       (customDrug) =>
            //         (customDrug?.Name?.toLowerCase() || "") ===
            //         (apiDrug?.Name?.toLowerCase() || ""),
            //     ),
            // );
            setDrugsData(data);
            setIsLoading(false);
          }}
          renderSkeleton={
            <div className="ps-8">
              <MedicationLoader />
            </div>
          }
          noDataElement={
            <>
              <img src={secondImage} alt="No Results" />
              <p className="text-body-text text-center">{t("no_results")}</p>

              {hasSearched && currentSearchValue.trim() && !isLoading && (
                <div className="mt-4 flex flex-col items-center gap-3">
                  <button
                    onClick={handleAddCustomDrug}
                    className="hover:bg-primary-primary-text rounded-lg bg-primary px-6 py-2 text-white transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
                  >
                    {t("add") + " " + currentSearchValue}
                  </button>
                </div>
              )}
            </>
          }
        />
      </div>
    </div>
  );
};

export default AddPrescriptionSearchDrug;
